// O11 · Save your progress (QO11_Save): email + password + "Let {n} send reminders" -> POST /api/auth/register
// (RegisterRequest from the draft) -> Chat in 'first' mode.
import { useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { Screen, XScene, XFace, PrimaryButton, TextField, SwitchRow, ScrollArea } from '../../components';
import { errorText, enableReminders, register } from '../../store';
import { reset } from '../../router';
import { ApiError } from '../../api/client';
import { clearDraft, registerRequest, setDraft, useDraft } from './draft';
import s from './O11Save.module.css';

export function O11Save() {
  const d = useDraft((x) => x);
  const ch: CharId = d.ch || 'margo';
  const c = C[ch];
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState<{ field: 'email' | 'pass' | null; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setErr({ field: 'email', text: 'That email looks off.' }); return; }
    if (pass.length < 6) { setErr({ field: 'pass', text: 'Use at least 6 characters for the password.' }); return; }
    setBusy(true); setErr(null);
    try {
      await register(registerRequest(e, pass));
      const remind = d.remind;
      clearDraft();
      reset('C', { chat: 'first' });
      if (remind) void enableReminders(); // permission prompt + device registration (mobile shells)
    } catch (x) {
      const field = x instanceof ApiError && /email/.test(x.code) ? 'email' : x instanceof ApiError && /password/.test(x.code) ? 'pass' : null;
      setErr({ field, text: errorText(x) });
    } finally { setBusy(false); }
  };

  return (
    <Screen theme={ch} id="O11">
      <ScrollArea class={s.scroll} fadeTop={0}>
        <div class={s.top} aria-hidden="true">
          <XScene ch={ch} stage={1} mode="dim" video={false} alt="" />
          <div class={s.topFade} />
        </div>
        <div class={s.lead} />
        <div class={s.face}><XFace ch={ch} e={4} s={76} ring="var(--acc)" alt="" /></div>
        <div class={s.head}>
          <h1 class={s.h1}>Save your progress</h1>
          <p class={s.sub}>{`So ${c.name} remembers today, on your phone and computer.`}</p>
        </div>
        <form class={s.form} onSubmit={(ev) => { ev.preventDefault(); void save(); }}>
          <TextField
            label="Email" type="email" value={email} onInput={(v) => { setEmail(v); setErr(null); }} placeholder="you@email.com"
            autoComplete="email" inputMode="email" autoCapitalize="none" enterKeyHint="next" invalid={err?.field === 'email'}
            hint={err?.field === 'email' ? err.text : undefined}
          />
          <TextField
            label="Password" type="password" value={pass} onInput={(v) => { setPass(v); setErr(null); }} autoComplete="new-password"
            enterKeyHint="go" onEnter={() => void save()} invalid={err?.field === 'pass'} hint={err?.field === 'pass' ? err.text : undefined}
            labelStyle={{ marginTop: 4 }}
          />
          <SwitchRow
            style={{ marginTop: 8 }} checked={d.remind} onChange={(on) => setDraft({ remind: on })}
            title={`Let ${c.name} send reminders`}
            sub={<>Only at times you agreed to. Quiet <span class="xm-nowrap">10 PM–8 AM.</span></>}
          />
          {err && !err.field && <p class={s.error} role="alert">{err.text}</p>}
        </form>
        <div class={s.grow} />
      </ScrollArea>
      <div class={s.actions}>
        <PrimaryButton onClick={() => void save()} busy={busy} data-cta="save">Save and finish</PrimaryButton>
        <p class={s.footer}>{`Invite ${d.invite || 'XM-7Q4K'} · Forgot your password later? The team can reset it.`}</p>
      </div>
    </Screen>
  );
}
